import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardActions, 
  Typography, 
  Button, 
  Chip, 
  Box, 
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileUploadIcon from '@mui/icons-material/FileUpload';

const SequenceCard = ({ 
  sequence, 
  onEdit, 
  onDelete, 
  onExecute,
  isExecuting 
}) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenUploadDialog = () => {
    setUploadDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleExecute = () => {
    if (selectedFile) {
      onExecute(sequence.id, selectedFile);
      handleCloseUploadDialog();
    }
  };

  const handleEdit = () => {
    onEdit(sequence);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete(sequence.id);
    handleMenuClose();
  };

  // Determinar el tipo de entrada del primer paso
  const firstStepInputType = sequence.steps.length > 0 ? sequence.steps[0].inputType : 'unknown';
  
  // Determinar el tipo de salida del último paso
  const lastStepOutputType = sequence.steps.length > 0 
    ? sequence.steps[sequence.steps.length - 1].outputType 
    : 'unknown';

  return (
    <>
      <Card 
        variant="outlined" 
        sx={{ 
          minWidth: 275, 
          marginBottom: 2,
          position: 'relative'
        }}
      >
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="h6" component="div" gutterBottom>
              {sequence.name}
            </Typography>
            <IconButton
              aria-label="more"
              id={`sequence-menu-button-${sequence.id}`}
              aria-controls={menuOpen ? `sequence-menu-${sequence.id}` : undefined}
              aria-expanded={menuOpen ? 'true' : undefined}
              aria-haspopup="true"
              onClick={handleMenuClick}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {sequence.description || 'Sin descripción'}
          </Typography>
          
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip 
              label={`Entrada: ${firstStepInputType}`} 
              size="small" 
              color="primary" 
              variant="outlined" 
            />
            <Chip 
              label={`Salida: ${lastStepOutputType}`} 
              size="small" 
              color="secondary" 
              variant="outlined" 
            />
            <Chip 
              label={`${sequence.steps.length} pasos`} 
              size="small" 
              variant="outlined" 
            />
          </Stack>
          
          <Typography variant="body2" color="text.secondary">
            Pasos:
          </Typography>
          
          <Box sx={{ mt: 1 }}>
            {sequence.steps.map((step, index) => (
              <Box key={index} display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="body2" component="span" sx={{ mr: 1 }}>
                  {index + 1}.
                </Typography>
                <Chip 
                  label={step.type} 
                  size="small" 
                  sx={{ mr: 1 }} 
                />
                {step.operation && (
                  <Typography variant="caption" color="text.secondary">
                    (Operación: {step.operation})
                  </Typography>
                )}
                {step.language && (
                  <Typography variant="caption" color="text.secondary">
                    (Idioma: {step.language})
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </CardContent>
        
        <CardActions>
          <Button 
            startIcon={<PlayArrowIcon />}
            size="small" 
            color="primary"
            onClick={handleOpenUploadDialog}
            disabled={isExecuting}
          >
            Ejecutar
          </Button>
        </CardActions>
      </Card>
      
      <Menu
        id={`sequence-menu-${sequence.id}`}
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>
      
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog}>
        <DialogTitle>Ejecutar secuencia: {sequence.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
            Selecciona un archivo de entrada ({firstStepInputType}):
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <input
              type="file"
              id="sequence-input-file"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="sequence-input-file">
              <Button
                variant="outlined"
                component="span"
                startIcon={<FileUploadIcon />}
              >
                Seleccionar archivo
              </Button>
            </label>
            
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Archivo seleccionado: {selectedFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>Cancelar</Button>
          <Button 
            onClick={handleExecute} 
            disabled={!selectedFile}
            color="primary"
          >
            Ejecutar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SequenceCard; 